import { getSupabase } from './client';
import type { Database, Json, MemberRole, Tables } from './database.types';
import { toDataError } from './errors';
import { isJsonObject } from './json';

export type PermAction = 'read' | 'create' | 'update' | 'delete';
export type ModuleGrants = Record<string, PermAction[]>;
export type Grants = '*' | ModuleGrants;
export type MenuOverrides = Record<string, boolean>;

export const PERM_ACTIONS: readonly PermAction[] = ['read', 'create', 'update', 'delete'];

export interface Workspace {
  readonly id: string;
  readonly slug: string;
  readonly name: string;
  readonly isDemo: boolean;
}

export interface WorkspaceMember {
  readonly workspaceId: string;
  readonly userId: string;
  readonly email: string;
  readonly displayName: string;
  readonly role: MemberRole;
  readonly empCode: string | null;
  readonly active: boolean;
  readonly grants: Grants;
  readonly menus: MenuOverrides | null;
}

export interface WorkspaceScope {
  readonly workspaceId: string;
  readonly appKey: string;
}

export interface WorkspaceContext extends WorkspaceScope {
  readonly workspace: Workspace;
  readonly member: WorkspaceMember;
}

function isPermAction(value: unknown): value is PermAction {
  return value === 'read' || value === 'create' || value === 'update' || value === 'delete';
}

export function parseGrants(value: Json | undefined): Grants {
  if (value === '*') return '*';
  if (!isJsonObject(value)) return {};
  const grants: ModuleGrants = {};
  for (const [module, actions] of Object.entries(value)) {
    if (Array.isArray(actions)) grants[module] = actions.filter(isPermAction);
  }
  return grants;
}

export function parseMenus(value: Json | undefined): MenuOverrides | null {
  if (!isJsonObject(value)) return null;
  const menus: MenuOverrides = {};
  for (const [key, visible] of Object.entries(value)) {
    if (typeof visible === 'boolean') menus[key] = visible;
  }
  return menus;
}

export function grantsToJson(grants: Grants): Json {
  if (grants === '*') return '*';
  const out: { [module: string]: Json } = {};
  for (const [module, actions] of Object.entries(grants)) out[module] = [...actions];
  return out;
}

/** Client-side mirror of private.can() — for UI decisions only; the database re-checks. */
export function memberCan(member: WorkspaceMember | null, permModule: string, action: PermAction): boolean {
  if (!member || !member.active) return false;
  if (member.role === 'admin' || member.grants === '*') return true;
  const actions = member.grants[permModule] ?? [];
  return action === 'read' ? actions.length > 0 : actions.includes(action);
}

function toMember(
  row: Tables<'workspace_members'>,
  access: Tables<'member_app_access'> | undefined,
): WorkspaceMember {
  return {
    workspaceId: row.workspace_id,
    userId: row.user_id,
    email: row.email,
    displayName: row.display_name,
    role: row.role,
    empCode: row.emp_code,
    active: row.active,
    grants: parseGrants(access?.grants),
    menus: parseMenus(access?.menus ?? undefined),
  };
}

/**
 * Finds the workspace the signed-in user belongs to for this dashboard.
 * Returns null when the user has no active membership (→ caller signs them out).
 */
export async function resolveWorkspace(
  userId: string,
  appKey: string,
  preferredSlug: string | null,
): Promise<WorkspaceContext | null> {
  const supabase = getSupabase();

  const { data: memberships, error } = await supabase
    .from('workspace_members')
    .select('*')
    .eq('user_id', userId)
    .eq('active', true);
  if (error) throw toDataError(error);
  if (!memberships || memberships.length === 0) return null;

  const workspaceIds = memberships.map((m) => m.workspace_id);
  // Both queries below only need workspaceIds from the membership lookup above, not each other's
  // result, so they run in parallel instead of one more sequential round-trip after the other.
  const [{ data: workspaces, error: wsError }, { data: accessRows, error: accessError }] = await Promise.all([
    supabase.from('workspaces').select('*').in('id', workspaceIds),
    supabase.from('member_app_access').select('*').in('workspace_id', workspaceIds).eq('user_id', userId).eq('app_key', appKey),
  ]);
  if (wsError) throw toDataError(wsError);
  if (accessError) throw toDataError(accessError);

  const workspace =
    (preferredSlug ? workspaces?.find((w) => w.slug === preferredSlug) : workspaces?.[0]) ?? null;
  if (!workspace) return null;

  const membership = memberships.find((m) => m.workspace_id === workspace.id);
  if (!membership) return null;

  const access = accessRows?.find((a) => a.workspace_id === workspace.id);

  return {
    workspaceId: workspace.id,
    appKey,
    workspace: { id: workspace.id, slug: workspace.slug, name: workspace.name, isDemo: workspace.is_demo },
    member: toMember(membership, access ?? undefined),
  };
}

/**
 * Every company (workspace) the signed-in user is an active member of, so the UI can offer a
 * switch. Returns one entry for a single-company user — the header hides the switcher then.
 */
export async function listMyWorkspaces(userId: string): Promise<Workspace[]> {
  const supabase = getSupabase();
  const { data: memberships, error } = await supabase
    .from('workspace_members')
    .select('workspace_id')
    .eq('user_id', userId)
    .eq('active', true);
  if (error) throw toDataError(error);
  const ids = (memberships ?? []).map((m) => m.workspace_id);
  if (ids.length === 0) return [];

  const { data, error: wsError } = await supabase.from('workspaces').select('*').in('id', ids).order('name');
  if (wsError) throw toDataError(wsError);
  return (data ?? []).map((w) => ({ id: w.id, slug: w.slug, name: w.name, isDemo: w.is_demo }));
}

export async function listMembers(scope: WorkspaceScope): Promise<WorkspaceMember[]> {
  const supabase = getSupabase();
  const [membersResult, accessResult] = await Promise.all([
    supabase.from('workspace_members').select('*').eq('workspace_id', scope.workspaceId).order('created_at'),
    supabase
      .from('member_app_access')
      .select('*')
      .eq('workspace_id', scope.workspaceId)
      .eq('app_key', scope.appKey),
  ]);
  if (membersResult.error) throw toDataError(membersResult.error);
  if (accessResult.error) throw toDataError(accessResult.error);

  const accessByUser = new Map((accessResult.data ?? []).map((a) => [a.user_id, a]));
  return (membersResult.data ?? []).map((row) => toMember(row, accessByUser.get(row.user_id)));
}

export interface MemberProfilePatch {
  displayName?: string;
  role?: MemberRole;
  empCode?: string | null;
}

/** Admin only (RLS). `active` is intentionally not editable here — use the admin-users function. */
export async function updateMemberProfile(scope: WorkspaceScope, userId: string, patch: MemberProfilePatch): Promise<void> {
  const update: Database['public']['Tables']['workspace_members']['Update'] = {};
  if (patch.displayName !== undefined) update.display_name = patch.displayName;
  if (patch.role !== undefined) update.role = patch.role;
  if (patch.empCode !== undefined) update.emp_code = patch.empCode && patch.empCode.trim() ? patch.empCode.trim() : null;
  if (Object.keys(update).length === 0) return;

  const { error } = await getSupabase()
    .from('workspace_members')
    .update(update)
    .eq('workspace_id', scope.workspaceId)
    .eq('user_id', userId);
  if (error) throw toDataError(error);
}

/** Admin only (RLS). Upserts the per-dashboard permission grants and menu overrides. */
export async function upsertMemberAccess(
  scope: WorkspaceScope,
  userId: string,
  access: { grants: Grants; menus: MenuOverrides | null },
): Promise<void> {
  const { error } = await getSupabase()
    .from('member_app_access')
    .upsert(
      {
        workspace_id: scope.workspaceId,
        user_id: userId,
        app_key: scope.appKey,
        grants: grantsToJson(access.grants),
        menus: access.menus ? { ...access.menus } : null,
      },
      { onConflict: 'workspace_id,user_id,app_key' },
    );
  if (error) throw toDataError(error);
}

export async function listAppModules(appKey: string): Promise<Array<{ module: string; permModule: string }>> {
  const { data, error } = await getSupabase().from('app_modules').select('*').eq('app_key', appKey).order('module');
  if (error) throw toDataError(error);
  return (data ?? []).map((row) => ({ module: row.module, permModule: row.perm_module }));
}
